import { ForeignSyncMetaCreateInput, ForeignSyncMetaUpdateInput } from '@bp/bp-graphql-types';
import {
  ActionBar,
  Button,
  Checkbox,
  Dropdown,
  DropdownMenu,
  EmptyState,
  ImportIcon,
  LazyLoader,
  TeacherIllustration,
} from '@bp/ui-components';
import classNames from 'classnames';
import { BpCard } from 'components/BpCard/BpCard';
import { distance } from 'fastest-levenshtein';
import { Form, Formik } from 'formik';
import { SettingsImportPlanungSubpageOutletContext } from 'pages/Settings/subpages/SettingsImportPlanungSubpage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useMutation } from 'urql';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import { getFragmentData, graphql } from '../../../client/gql';
import { type ForeignSyncMetasFieldsFragment } from '../../../client/gql/graphql';
import { backendApi } from '../../../utils/backendApi';
import { createForeignRefId, SyncMaster } from '../../../utils/create-foreignRefId';
import {
  CreateForeignSyncMetaDocument,
  ForeignSyncMetasFieldsDocument,
  UpdateForeignSyncMetaDocument,
} from '../queries/queries';
import styles from './_PlanungImportForms.module.scss';
import { PlanungImportTeachersFormRow } from './PlanungImportTeachersFormRow';

const PlanungImportTeachersFormFragment = graphql(`
  fragment PlanungImportTeachersFormFields on Query {
    profiles(
      where: {
        OR: [{ organizationRoles_INCLUDES: "TEACHER" }, { organizationRoles_INCLUDES: "OTHER" }]
        organization: { uuid: $organizationUuid }
      }
    ) {
      __typename
      uuid
      listName
    }
  }
`);

type PlanungImportTeacherFormType = {
  teachers: TeacherModel[];
};

type TeacherModel = {
  uuid: string;
  selected: boolean;
  name: string;
  lastName: string;
  connectProfileUuid: string | null;
  connected: boolean;
  ignore: boolean;
};

export const PlanungImportTeachersForm = () => {
  const { t } = useTranslation();

  const { bpData, planungData, refetch, planungSchoolyear, bpSchoolyear } =
    useOutletContext<SettingsImportPlanungSubpageOutletContext>();

  const metaFragment = getFragmentData(ForeignSyncMetasFieldsDocument, bpData);
  const formFragment = getFragmentData(PlanungImportTeachersFormFragment, bpData);

  const foreignSyncMetas: Pick<ForeignSyncMetasFieldsFragment, 'foreignSyncMetas'>['foreignSyncMetas'] | undefined =
    useMemo(
      () => metaFragment?.foreignSyncMetas.filter((fsm) => !fsm.syncTarget || fsm.syncTarget?.__typename === 'Profile'),
      [metaFragment],
    );

  const profiles = formFragment?.profiles ?? [];
  const teachers = planungData?.teachers ?? [];

  const [, createForeignSyncMeta] = useMutation(CreateForeignSyncMetaDocument);
  const [, updateForeignSyncMeta] = useMutation(UpdateForeignSyncMetaDocument);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const [teacherModel, setTeacherModel] = useState<TeacherModel[]>([]);

  const handleImport = async () => {
    const response = await backendApi.post(
      `/planung/import/entities/teachers`,
      JSON.stringify({
        planungSchoolyear,
        bpSchoolyear,
      }),
    );
    return JSON.stringify(await response.json());
  };

  const handleSubmit = async () => {
    setLoading(true);

    const selectedTeachers = teacherModel.filter((t) => t.selected);

    const ignore = false; // this 'ignore' idea is not really well implemented. so we default it to false.

    const createInput = (teacher: TeacherModel, foreignRefId: string): ForeignSyncMetaCreateInput => ({
      foreignRefId: foreignRefId,
      syncSource: SyncMaster.planung,
      ignore,
      syncTarget: { Profile: { connect: { where: { node: { uuid: teacher.connectProfileUuid } } } } },
    });

    const updateInput = (teacher: TeacherModel): ForeignSyncMetaUpdateInput => ({
      ignore,
      syncTarget: teacher.connectProfileUuid
        ? { Profile: { disconnect: {}, connect: { where: { node: { uuid: teacher.connectProfileUuid } } } } }
        : { Profile: { disconnect: {} } },
    });

    let errors: number = 0;

    for (const teacher of selectedTeachers) {
      const foreignRefId = createForeignRefId(SyncMaster.planung, teacher.uuid);

      const hasForeignSyncMeta = !!foreignSyncMetas?.find((p) => p.foreignRefId === foreignRefId);
      if (hasForeignSyncMeta) {
        const { error } = await updateForeignSyncMeta({
          update: updateInput(teacher),
          where: { foreignRefId: foreignRefId },
        });
        error && errors++;
      } else {
        const { error } = await createForeignSyncMeta({ input: createInput(teacher, foreignRefId) });
        error && errors++;
      }
    }

    if (errors > 0) {
      showUserDbErrorToast();
    } else {
      await handleImport();
      showSuccessToast(t('planungImport.success'));
    }

    refetch();
    setLoading(false);
  };

  const initialValues: PlanungImportTeacherFormType = {
    // handle via state
    teachers: [],
  };

  function handleSelect(uuid: string, select: boolean) {
    const newModel = teacherModel.map((t) => ({
      ...t,
      selected: t.uuid === uuid ? select : t.selected,
      connectProfileUuid: t.uuid === uuid && !t.connectProfileUuid && !select ? null : t.connectProfileUuid,
    }));
    setTeacherModel(newModel);
  }

  function handleProfileSelect(uuid: string, connectProfileUuid: string | null) {
    const newModel = teacherModel.map((t) => ({
      ...t,
      connectProfileUuid: t.uuid === uuid ? connectProfileUuid : t.connectProfileUuid,
    }));
    setTeacherModel(newModel);
  }

  function handleSelectAll(select: boolean) {
    const newModel = teacherModel.map((t) => ({
      ...t,
      selected: filteredModel.includes(t) ? select : t.selected,
      connectProfileUuid: !t.connectProfileUuid && !select ? null : t.connectProfileUuid,
    }));
    setTeacherModel(newModel);
  }

  function handleSelectImported(select: boolean) {
    const newModel = teacherModel.map((t) => ({
      ...t,
      selected: filteredModel.includes(t) && t.connected ? select : t.selected,
      connectProfileUuid: !t.connectProfileUuid && !select ? null : t.connectProfileUuid,
    }));
    setTeacherModel(newModel);
  }

  function handleSelectNotImported(select: boolean) {
    const newModel = teacherModel.map((t) => ({
      ...t,
      selected: filteredModel.includes(t) && !t.connected ? select : t.selected,
      connectProfileUuid: !t.connectProfileUuid && !select ? null : t.connectProfileUuid,
    }));
    setTeacherModel(newModel);
  }

  const filteredModel = teacherModel
    .filter((t) => (!search ? true : t.name.toLowerCase().includes(search?.toLowerCase())))
    .sort((a, b) => {
      // imported
      if (a.connected !== b.connected) {
        return a.connected ? 1 : -1;
      }
      // sort by lastName
      return naturalStringSort(a.lastName, b.lastName);
    });

  const availableProfiles: { uuid: string; name: string }[] = useMemo(() => {
    const connectedProfileUuids = teacherModel
      .map((t) => t.connectProfileUuid)
      .filter((uuid): uuid is string => !!uuid);
    return profiles
      .filter((p) => p && !connectedProfileUuids.includes(p.uuid))
      .map((p) => ({ uuid: p.uuid, name: p.listName ?? '' }));
  }, [teacherModel, profiles]);

  useEffect(() => {
    const model: TeacherModel[] = teachers.map((t) => {
      const fsm = foreignSyncMetas?.find((fsm) => fsm.foreignRefId === createForeignRefId(SyncMaster.planung, t.uuid));
      const connected = !!fsm && !!fsm.syncTarget && fsm.syncTarget.__typename === 'Profile';
      const preSelectProfile = profiles.find(
        (p) =>
          p.listName === t.listName ||
          p.listName?.includes(t.listName) ||
          t.listName.includes(p.listName ?? '') ||
          distance(p.listName ?? '', t.listName ?? '') < 5,
      );
      return {
        uuid: t.uuid,
        selected: !connected,
        name: t.listName ?? '',
        lastName: t.lastName ?? '',
        connectProfileUuid: connected ? (fsm.syncTarget?.uuid ?? null) : (preSelectProfile?.uuid ?? null),
        connected: connected,
        ignore: !!fsm?.ignore && fsm.ignore,
      };
    });
    setTeacherModel(model);
  }, [teachers, profiles, foreignSyncMetas]);

  return (
    <Formik<PlanungImportTeacherFormType> initialValues={initialValues} onSubmit={() => handleSubmit()}>
      {() => (
        <Form className={styles['planung-import-form']}>
          {loading ? (
            <LazyLoader size='xxl' borderRadius='m' forceHeight='25vh' />
          ) : (
            <BpCard noPadding header={{ headline: t('planungImport.importTeachers') }}>
              <ActionBar
                isOnWhite
                onGlobalFilterChange={(value) => setSearch(value)}
                className={styles['action-bar']}
                extendedActionsLeft={
                  <div className={styles.overview}>
                    {t('planungImport.importOverview', {
                      sum: teacherModel.filter((t) => t.selected).length,
                      total: filteredModel.length,
                    })}
                  </div>
                }
                extendedActionsRight={
                  <>
                    <Dropdown
                      defaultOpen={false}
                      trigger={<Button hierarchy='tertiary'>{t('common.selection')}</Button>}
                    >
                      <DropdownMenu
                        data={[
                          { label: t('planungImport.selectNotImported'), onClick: () => handleSelectNotImported(true) },
                          {
                            label: t('planungImport.deselectNotImported'),
                            onClick: () => handleSelectNotImported(false),
                          },
                          { type: 'ruler' },
                          { label: t('planungImport.selectImported'), onClick: () => handleSelectImported(true) },
                          { label: t('planungImport.deselectImported'), onClick: () => handleSelectImported(false) },
                          { type: 'ruler' },
                          { label: t('common.selectAll'), onClick: () => handleSelectAll(true) },
                          { label: t('planungImport.deselectAll'), onClick: () => handleSelectAll(false) },
                        ]}
                      />
                    </Dropdown>
                    <Button
                      type='submit'
                      icon={<ImportIcon />}
                      disabled={teacherModel.filter((t) => t.selected).length === 0}
                    >
                      {t('planungImport.createTeachers')}
                    </Button>
                  </>
                }
              />

              <div className={classNames(styles.form, styles.teachers)}>
                {filteredModel.length === 0 ? (
                  <EmptyState
                    icon={<TeacherIllustration />}
                    title={t('teachers.noTeachersFound')}
                    forcedHeight='30vh'
                  />
                ) : (
                  <>
                    <div className={styles.header}>
                      <div className={styles.checkbox}>
                        <Checkbox
                          onChange={(event) => {
                            handleSelectAll(event.target.checked);
                          }}
                          checked={
                            filteredModel.length > 0 &&
                            filteredModel.filter((t) => t.selected).length === filteredModel.length
                          }
                          name={`toggle-teachers-all`}
                        />
                        <div>{t('planungImport.personInPlanung')}</div>
                      </div>
                      <div></div>
                      <div>{t('planungImport.personInBp')}</div>
                      <div>{t('planungImport.connected')}</div>
                    </div>
                    {filteredModel.map((t) => (
                      <PlanungImportTeachersFormRow
                        key={t.uuid}
                        uuid={t.uuid}
                        selected={t.selected}
                        name={t.name}
                        profiles={availableProfiles}
                        selectedProfile={
                          t.connectProfileUuid && profiles.find((p) => p.uuid === t.connectProfileUuid)
                            ? {
                                value: profiles.find((p) => p.uuid === t.connectProfileUuid)?.uuid ?? '',
                                label: profiles.find((p) => p.uuid === t.connectProfileUuid)?.listName ?? '',
                              }
                            : null
                        }
                        connected={t.connected}
                        onSelect={(isSelected) => handleSelect(t.uuid, isSelected)}
                        onProfileSelect={(profileUuid) => handleProfileSelect(t.uuid, profileUuid)}
                      />
                    ))}
                  </>
                )}
              </div>
            </BpCard>
          )}
        </Form>
      )}
    </Formik>
  );
};
